import { useEffect, useRef } from "react";

// Wraps a node's collapse/expand toggle so that removing/restoring its
// subtree doesn't leave the tree's scroll container visibly jumping (bug
// #1910): collapsing shrinks the tree's rendered size, the browser clamps
// the scroll container's scrollLeft/scrollTop to the new range on its own,
// and with nothing correcting for it the view snaps to wherever that clamp
// lands. This uses the toggled node's own box — which never unmounts
// across its own toggle — as a stable on-screen anchor: measure it before
// the toggle, let the reflow happen (two rAFs, matching the codebase's
// existing "wait for a repaint" pattern), then report exactly how far it
// moved so the caller can nudge scroll position back by that same amount.
//
// `scrollCorrection`, when given, is `{ begin, end }` (see
// orgTreeScrollCorrectionContext) — begin() is called BEFORE the toggle so
// even the browser's own immediate scrollLeft/scrollTop clamp is covered,
// not just the corrective write this hook makes afterward; end() applies
// the measured delta and re-enables scroll reporting on that managed
// container. This is what keeps the correction from being mistaken for a
// real user pan and mirrored onto a sibling tree (see OrgTreeCard).
// When `scrollCorrection` isn't given, this falls back to adjusting the
// nearest `[data-org-scroll-area]` ancestor directly, which is safe
// wherever nothing is listening for "real" scrolls on that container (e.g.
// the PAIC import preview's read-only tree, which has no sibling to keep
// in sync).
export const useCollapseScrollAnchor = (onToggle, scrollCorrection) => {
  const boxRef = useRef(null);
  const pendingFramesRef = useRef(new Set());
  // How many begin() calls from this node haven't yet been matched by an
  // end() — normally 0 or 1, but a fast double-toggle can leave two
  // corrections in flight at once.
  const inFlightCountRef = useRef(0);
  const scrollCorrectionRef = useRef(scrollCorrection);
  scrollCorrectionRef.current = scrollCorrection;

  // Cancels any correction(s) still waiting on their rAFs if this node
  // unmounts first (e.g. switching between the Current/Proposed tree right
  // after collapsing) — the DOM node they'd measure would be detached by
  // then. Also balances out any begin() that never reached its matching
  // end() because its rAFs got cancelled here — otherwise OrgTreeCard's
  // scroll-suppression count would stay armed forever, silently breaking
  // scroll-sync reporting on that card for the rest of the session.
  useEffect(
    () => () => {
      pendingFramesRef.current.forEach((id) => cancelAnimationFrame(id));
      pendingFramesRef.current.clear();
      while (inFlightCountRef.current > 0) {
        scrollCorrectionRef.current?.end(0, 0);
        inFlightCountRef.current -= 1;
      }
    },
    []
  );

  const handleToggle = () => {
    const box = boxRef.current;
    const directScrollArea = scrollCorrection ? null : box?.closest("[data-org-scroll-area]");
    if (!box || (!scrollCorrection && !directScrollArea)) {
      onToggle();
      return;
    }

    const before = box.getBoundingClientRect();
    if (scrollCorrection) {
      scrollCorrection.begin();
      inFlightCountRef.current += 1;
    }
    onToggle();

    const frame1 = requestAnimationFrame(() => {
      pendingFramesRef.current.delete(frame1);
      const frame2 = requestAnimationFrame(() => {
        pendingFramesRef.current.delete(frame2);
        const after = box.getBoundingClientRect();
        const deltaLeft = after.left - before.left;
        const deltaTop = after.top - before.top;
        if (scrollCorrection) {
          scrollCorrection.end(deltaLeft, deltaTop);
          inFlightCountRef.current -= 1;
        } else if (deltaLeft !== 0 || deltaTop !== 0) {
          directScrollArea.scrollLeft += deltaLeft;
          directScrollArea.scrollTop += deltaTop;
        }
      });
      pendingFramesRef.current.add(frame2);
    });
    pendingFramesRef.current.add(frame1);
  };

  return { boxRef, handleToggle };
};
